import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
          <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
        </div>
        <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
        <p className="text-sm text-foreground-500 mb-6">该文章可能已被删除或链接无效</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '狠狠撸',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://henhenlu.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://henhenlu.com/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://henhenlu.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SchemaOrg data={articleSchema} />
      <SchemaOrg data={breadcrumbSchema} />

      <Breadcrumb items={breadcrumbItems} />

      <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8">
        <header className="mb-8">
          <Link
            to={`/category/${article.category}`}
            className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium mb-4 hover:bg-primary-100 transition-colors"
          >
            {article.categoryName}
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {article.viewCount.toLocaleString()} 阅读
            </span>
          </div>
        </header>

        <div className="mb-10 rounded-xl overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-auto object-cover"
          />
        </div>

        <div
          className="prose prose-base max-w-none text-foreground-800 leading-relaxed
            prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-8 prose-h2:mb-4 prose-h2:font-heading
            prose-p:text-sm prose-p:leading-relaxed prose-p:mb-4
            prose-ul:text-sm prose-ul:mb-4
            prose-li:text-sm prose-li:mb-1.5
            prose-strong:text-foreground-900 prose-strong:font-semibold"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="mt-8 pt-6 border-t border-background-200">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500 mr-2">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-secondary-50 text-secondary-700 rounded-full text-xs font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="mt-14">
            <h3 className="text-lg font-bold text-foreground-950 font-heading mb-6">
              相关推荐
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((item) => (
                <Link
                  key={item.slug}
                  to={`/article/${item.slug}`}
                  className="group p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="h-36 rounded-lg overflow-hidden mb-3">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors mb-2">
                    {item.title}
                  </h4>
                  <p className="text-xs text-foreground-500 line-clamp-2">{item.excerpt}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
}